/**
 * usePushNotifications
 *
 * React hook to manage browser Web Push subscription with ChantierFlow backend.
 * - Registers /push-sw.js service worker lazily
 * - Fetches VAPID public key from backend
 * - Subscribes or unsubscribes via PushManager
 * - Persists subscription on /api/push/subscribe and /api/push/unsubscribe
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

export const usePushNotifications = () => {
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "default"
  );
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const ok =
      typeof window !== "undefined" &&
      "serviceWorker" in navigator &&
      "PushManager" in window &&
      "Notification" in window;
    setSupported(ok);
    if (ok) {
      // Check existing subscription
      navigator.serviceWorker.getRegistration("/push-sw.js").then(async (reg) => {
        if (!reg) return;
        const sub = await reg.pushManager.getSubscription();
        setSubscribed(!!sub);
      });
    }
  }, []);

  const registerSW = useCallback(async () => {
    const reg = await navigator.serviceWorker.register("/push-sw.js");
    // Wait until it's active
    if (reg.installing) {
      await new Promise((resolve) => {
        reg.installing.addEventListener("statechange", (e) => {
          if (e.target.state === "activated") resolve();
        });
      });
    }
    return reg;
  }, []);

  const subscribe = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      if (!supported) throw new Error("Votre navigateur ne supporte pas les notifications push");
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") throw new Error("Permission refusée");

      const reg = await registerSW();
      const { data } = await api.getPushVapidKey();
      const key = urlBase64ToUint8Array(data.publicKey);

      // Clean any stale subscription (keys might differ after rotation)
      const existing = await reg.pushManager.getSubscription();
      if (existing) await existing.unsubscribe();

      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: key,
      });
      const json = sub.toJSON();
      await api.subscribePush({
        endpoint: json.endpoint,
        keys: { auth: json.keys.auth, p256dh: json.keys.p256dh },
        user_agent: navigator.userAgent,
      });
      setSubscribed(true);
      return true;
    } catch (e) {
      setError(e?.message || String(e));
      return false;
    } finally {
      setLoading(false);
    }
  }, [supported, registerSW]);

  const unsubscribe = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration("/push-sw.js");
      if (reg) {
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          await api.unsubscribePush({ endpoint: sub.endpoint });
          await sub.unsubscribe();
        }
      }
      setSubscribed(false);
      return true;
    } catch (e) {
      setError(e?.message || String(e));
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const sendTest = useCallback(async () => {
    setError(null);
    try {
      await api.sendPushTest({
        title: "Bati Assistant",
        body: "Notification de test reçue avec succès 🎉",
      });
      return true;
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
      return false;
    }
  }, []);

  return { supported, permission, subscribed, loading, error, subscribe, unsubscribe, sendTest };
};

export default usePushNotifications;
